import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  type AnyRoute,
} from '@tanstack/react-router';
import { getRuntimeConfig, type PermissionChecker, type RoutePermissionData } from '@eis/core';
import { AppShell } from '../chrome/AppShell';
import { activeModuleRegistry } from './modules';
import { HomePage } from '../pages/HomePage';
import { ForbiddenPage } from '../pages/ForbiddenPage';
import { NotFoundPage } from '../pages/NotFoundPage';

export interface RouterContext {
  /** Evaluated by beforeLoad; the same declarations filter the nav. */
  readonly permissions: PermissionChecker;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: AppShell,
  notFoundComponent: NotFoundPage,
});

/**
 * Route guard (Addendum A.2, enforcement layer 1).
 *
 * Declared ONCE per route in `staticData.permissions`, evaluated here, and reused by the nav
 * filter — so there is no second list of permissions to drift. A denied route redirects to the
 * shared 403 pattern rather than each module inventing its own no-access screen.
 */
export function guardRoute(context: RouterContext, staticData: unknown): void {
  const data = staticData as RoutePermissionData | undefined;
  if (data?.permissions === undefined) return;
  if (!context.permissions.canGroups(data.permissions)) {
    // TanStack Router signals navigation by throwing this sentinel; it is control flow, not an
    // error, which is why the generic "throw an Error" rule does not apply here.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/403' });
  }
}

/**
 * Mounts each module's route tree under its reserved namespace.
 *
 * The module exports a `createRoutes(parentRoute)` factory, so it never needs to know where in
 * the tree it lives — the shell owns composition (backbone §0.2). Each namespace is a layout
 * route, which is what gives the module its own boundary in the tree.
 */
export async function buildModuleRoutes(rootRoute: AnyRoute): Promise<AnyRoute[]> {
  const trees = await Promise.all(
    activeModuleRegistry().modules.map(async (module) => {
      // A route carries either an id or a path, never both — the basePath IS the module's
      // identity in the tree.
      const namespaceRoute = createRoute({
        getParentRoute: () => rootRoute,
        path: module.basePath,
      });
      const { createRoutes } = await module.routes();
      return namespaceRoute.addChildren(createRoutes(namespaceRoute)) as AnyRoute;
    }),
  );
  return trees;
}

/** Returned type is inferred so TanStack's route-tree typing survives to the consumers. */
export async function buildRouter() {
  const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    // A host with a selected module set (the Data Suite, T-59) has no home page of its own: `/`
    // leads to its first menu entry (SDD-024 A-5). Without `modules` the console's home is unchanged.
    beforeLoad: ({ context }) => {
      const target = homeRedirect(context);
      if (target !== undefined) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ href: target, replace: true });
      }
    },
    component: HomePage,
  });

  const forbiddenRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/403',
    component: ForbiddenPage,
  });

  const notFoundRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/404',
    component: NotFoundPage,
  });

  const moduleRoutes = await buildModuleRoutes(rootRoute);
  const routeTree = rootRoute.addChildren([
    indexRoute,
    forbiddenRoute,
    notFoundRoute,
    ...moduleRoutes,
  ]);

  return createRouter({
    routeTree,
    context: { permissions: denyAll },
    defaultPreload: 'intent',
    defaultNotFoundComponent: NotFoundPage,
    scrollRestoration: true,
  });
}

/** Where `/` leads on a host whose `config.json` names `modules` (T-59); `undefined` elsewhere. */
export function homeRedirect(context: RouterContext): string | undefined {
  let modules: readonly string[] | undefined;
  try {
    modules = getRuntimeConfig().modules;
  } catch {
    return undefined;
  }
  if (modules === undefined) return undefined;
  return activeModuleRegistry().homePath(context.permissions);
}

/** The router is created before auth resolves; until then nothing is permitted. */
const denyAll: PermissionChecker = {
  can: () => false,
  canAny: () => false,
  canAll: () => false,
  canGroups: () => false,
};
